import {useEffect} from "react";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import {useQueryClient} from "react-query";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useCreateMutation, useGetItemQuery} from "../../../hooks/useQueryHooks";
import CategoryForm from "./CategoryForm";

import * as CategoryApi from "../../../api/educationApi/educationCategoryApi";
import {ResponseItem} from "../../../models/clientModels/interfaces/Response";
import * as Params from "../../../models/clientModels/interfaces/apiParams";
import {EDCategory, initialEDCategory} from "../../../models/serverModels/education/EDCategory";

import {useRecoilValue, useSetRecoilState} from "recoil";
import {tokenSelector} from "../../../Recoil/Atom/authState";
import {snackBarState, snackBarToggleState} from "../../../Recoil/Atom/snackBarState";
import ConfirmButtonGroup from "../../../components/ConfirmButtonGroup";
import MWProgress from "../../../components/MWProgress";
import SubTitle from "../../../components/SubTitle";
import {FormSection} from "../../../styledComponents/StyledCreateForm";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";

const categorySchema = yup.object().shape({
  name: yup.string().required("카테고리를 입력하세요"),
});

const UpdateCategory = () => {
  const {param1, param2} = useParams();
  const depth = parseInt(param1!);
  const parentUID = parseInt(param2!);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state;

  const tokenState = useRecoilValue(tokenSelector);

  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const {
    data: category,
    isLoading,
    isFetching,
  } = useGetItemQuery<ResponseItem<EDCategory>>({
    key: ["EducationCategory", id],
    api: () => CategoryApi.getItem({id, token: tokenState}),
  });

  const {mutate: updateMutate} = useCreateMutation<
    ResponseItem<EDCategory>,
    Params.Update<EDCategory>
  >({
    api: CategoryApi.update,
    options: {
      onSuccess: () => {
        setSnackBarToggle(true);
        setSnackBarState("update");
        switch (depth) {
          case 0:
            navigate(`/Education/CategoryList`);
            break;
          case 1:
            navigate(`/Education/MiddleCategoryList/${parentUID}`);
            break;
          case 2:
            navigate(`/Education/SubCategoryList/${id}`);
            break;
        }
        queryClient.invalidateQueries(["EducationCategoryList"]);
      },
    },
  });

  const methods = useForm<EDCategory>({
    defaultValues: category?.item || initialEDCategory,
    resolver: yupResolver(categorySchema),
  });

  const {reset} = methods;

  useEffect(() => {
    reset({...category?.item});
  }, [category?.item, reset]);

  const onSubmit: SubmitHandler<EDCategory> = (data) => {
    updateMutate({
      id,
      token: tokenState,
      item: {...initialEDCategory, ...data, depth, parentUID},
    });
  };

  return (
    <FormProvider {...methods}>
      <SubTitle subTitle="분류목록 수정" />
      {isLoading && <MWProgress />}
      <FormSection>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <CategoryForm captionColor="#546E7A" />
          <ConfirmButtonGroup link="/Education/CategoryList" cancelText="취소" confirmText="수정" />
        </form>
      </FormSection>
    </FormProvider>
  );
};

export default UpdateCategory;
